import { FormEvent } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { BackofficeUser, RoleInfo, UserSortKey } from '../../types';
import {
  UserMobileCard,
  UsersTable,
  RegisterUserDialog,
  EditRolesDialog,
  ResetPasswordDialog,
  DeleteUserDialog
} from './users';

interface UsersPageProps {
  // User list state
  userSearch: string;
  setUserSearch: (search: string) => void;
  usersLoading: boolean;
  sortedUsers: BackofficeUser[];
  usersSort: { column: UserSortKey; direction: 'asc' | 'desc' };
  toggleUsersSort: (column: UserSortKey) => void;

  // Current user info
  currentUserId: string | undefined;

  // Role catalog
  roleCatalog: RoleInfo[];

  // Register dialog state
  registerDialogOpen: boolean;
  setRegisterDialogOpen: (open: boolean) => void;
  registerName: string;
  setRegisterName: (name: string) => void;
  registerUsername: string;
  setRegisterUsername: (username: string) => void;
  registerEmail: string;
  setRegisterEmail: (email: string) => void;
  registerPassword: string;
  setRegisterPassword: (password: string) => void;
  registerRoles: string[];
  toggleRole: (roleId: string) => void;
  registerError?: string;
  registerSuccess?: string;
  registerLoading: boolean;

  // Edit roles dialog state
  selectedUser: BackofficeUser | null;
  openEditRolesDialog: (user: BackofficeUser) => void;
  openResetPasswordDialog: (user: BackofficeUser) => void;
  openDeleteUserDialog: (user: BackofficeUser) => void;
  editRolesDialogOpen: boolean;
  setEditRolesDialogOpen: (open: boolean) => void;
  editRoles: string[];
  setEditRoles: React.Dispatch<React.SetStateAction<string[]>>;
  editRolesLoading: boolean;

  // Reset password dialog state
  resetPasswordDialogOpen: boolean;
  setResetPasswordDialogOpen: (open: boolean) => void;
  newPassword: string;
  setNewPassword: (password: string) => void;
  resetPasswordLoading: boolean;

  // Delete user dialog state
  deleteUserDialogOpen: boolean;
  setDeleteUserDialogOpen: (open: boolean) => void;
  deleteUserLoading: boolean;

  // Handlers
  fetchUsers: () => Promise<{ success: boolean; error?: string }>;
  handleRegisterSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleEditRolesSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleResetPasswordSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleToggleUserStatus: () => Promise<void>;
  handleDeleteUser: () => Promise<void>;
}

export function UsersPage({
  userSearch,
  setUserSearch,
  usersLoading,
  sortedUsers,
  usersSort,
  toggleUsersSort,
  currentUserId,
  roleCatalog,
  registerDialogOpen,
  setRegisterDialogOpen,
  registerName,
  setRegisterName,
  registerUsername,
  setRegisterUsername,
  registerEmail,
  setRegisterEmail,
  registerPassword,
  setRegisterPassword,
  registerRoles,
  toggleRole,
  registerError,
  registerSuccess,
  registerLoading,
  selectedUser,
  openEditRolesDialog,
  openResetPasswordDialog,
  openDeleteUserDialog,
  editRolesDialogOpen,
  setEditRolesDialogOpen,
  editRoles,
  setEditRoles,
  editRolesLoading,
  resetPasswordDialogOpen,
  setResetPasswordDialogOpen,
  newPassword,
  setNewPassword,
  resetPasswordLoading,
  deleteUserDialogOpen,
  setDeleteUserDialogOpen,
  deleteUserLoading,
  fetchUsers,
  handleRegisterSubmit,
  handleEditRolesSubmit,
  handleResetPasswordSubmit,
  handleDeleteUser
}: UsersPageProps) {
  return (
    <>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Administracao</span>
        <span>/</span>
        <span className="font-medium text-foreground">Gestao de usuarios</span>
      </div>

      <Card className="shadow-sm">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <CardTitle>Usuarios do sistema</CardTitle>
            <CardDescription>
              Gerencie os usuarios com acesso ao backoffice.
            </CardDescription>
          </div>

          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <Input
              placeholder="Buscar por nome, username ou e-mail"
              value={userSearch}
              onChange={(event) => setUserSearch(event.target.value)}
              className="w-full sm:w-64"
            />

            <div className="flex flex-wrap gap-2">
              <RegisterUserDialog
                open={registerDialogOpen}
                onOpenChange={setRegisterDialogOpen}
                name={registerName}
                setName={setRegisterName}
                username={registerUsername}
                setUsername={setRegisterUsername}
                email={registerEmail}
                setEmail={setRegisterEmail}
                password={registerPassword}
                setPassword={setRegisterPassword}
                roles={registerRoles}
                toggleRole={toggleRole}
                roleCatalog={roleCatalog}
                error={registerError}
                success={registerSuccess}
                loading={registerLoading}
                onSubmit={handleRegisterSubmit}
              />

              <Button type="button" onClick={fetchUsers} disabled={usersLoading} className="flex-1 sm:flex-initial">
                <RefreshCw
                  className={cn('mr-2 h-4 w-4', usersLoading && 'animate-spin')}
                />
                <span className="hidden md:inline">{usersLoading ? 'Atualizando' : 'Recarregar'}</span>
                <span className="md:hidden">Atualizar</span>
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {usersLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin" />
              </div>
            ) : sortedUsers.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-8">
                Nenhum usuario encontrado.
              </p>
            ) : (
              sortedUsers.map((user) => (
                <UserMobileCard
                  key={user.id}
                  user={user}
                  roleCatalog={roleCatalog}
                  currentUserId={currentUserId}
                  onEditRoles={openEditRolesDialog}
                  onResetPassword={openResetPasswordDialog}
                  onDelete={openDeleteUserDialog}
                />
              ))
            )}
          </div>

          {/* Desktop Table View */}
          <UsersTable
            users={sortedUsers}
            usersLoading={usersLoading}
            roleCatalog={roleCatalog}
            currentUserId={currentUserId}
            usersSort={usersSort}
            toggleUsersSort={toggleUsersSort}
            onEditRoles={openEditRolesDialog}
            onResetPassword={openResetPasswordDialog}
            onDelete={openDeleteUserDialog}
          />
        </CardContent>
      </Card>

      {/* Edit Roles Dialog */}
      <EditRolesDialog
        open={editRolesDialogOpen}
        onOpenChange={setEditRolesDialogOpen}
        selectedUser={selectedUser}
        roleCatalog={roleCatalog}
        editRoles={editRoles}
        setEditRoles={setEditRoles}
        loading={editRolesLoading}
        onSubmit={handleEditRolesSubmit}
      />

      {/* Reset Password Dialog */}
      <ResetPasswordDialog
        open={resetPasswordDialogOpen}
        onOpenChange={setResetPasswordDialogOpen}
        selectedUser={selectedUser}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        loading={resetPasswordLoading}
        onSubmit={handleResetPasswordSubmit}
      />

      {/* Delete User Dialog */}
      <DeleteUserDialog
        open={deleteUserDialogOpen}
        onOpenChange={setDeleteUserDialogOpen}
        selectedUser={selectedUser}
        loading={deleteUserLoading}
        onDelete={handleDeleteUser}
      />

      <section className="pb-6 text-center text-xs text-muted-foreground">
        Ultima atualizacao: {new Date().toLocaleString()}
      </section>
    </>
  );
}
